/**
 * Ask the browser to keep this origin's data instead of treating it as disposable cache.
 *
 * iOS Safari clears all script-writable storage for a site a visitor has not opened in 7 days. For
 * the free web build that is the whole journey — mastery, records, the lesson they were on — gone
 * after a fortnight's holiday, on a device nobody touched. The TWA and the iOS shell are exempt
 * from that sweep, and an installed PWA is usually granted persistence automatically, so this
 * mainly buys the browser visitor a journey that survives being put down.
 *
 * Asked at the first real save rather than at boot, deliberately:
 *   • Chrome and Safari answer silently from engagement signals, and a request made before the
 *     player has done anything is the one most likely to be turned down.
 *   • Firefox asks the USER. A permission doorhanger is bad enough; one on a cold start, in front
 *     of a child who has not played yet, would be the first thing the game ever said to them.
 * By the first flush there is progress worth the question.
 *
 * Best-effort throughout — never awaited, every failure swallowed, and `navigator.storage` is
 * simply absent on older WebViews. Nothing in the game may depend on the answer.
 */
let asked = false;

export function requestPersistentStorage(): void {
  if (asked) return;
  asked = true;
  try {
    const store = navigator?.storage;
    if (!store?.persist || !store.persisted) return;
    // `persisted()` first: already-granted is the common case on an installed app, and asking
    // again there is a prompt (on Firefox) for something we have.
    void store.persisted()
      .then((already) => (already ? undefined : store.persist().then(() => undefined)))
      .catch(() => { /* refused, or the API threw — the game is unaffected either way */ });
  } catch {
    /* no navigator / no storage API */
  }
}
